<template>
  <SMBottomBar>
    <template #logo>
      <img width="90" src="@/assets/img/logo.png" alt="SquareMaze" />
    </template>

    <SMBottomBarItem v-if="isInsideZoneLayout" class="red-button" icon="arrow_back" @click="exitZone()">
      Exit Section
    </SMBottomBarItem>
    <SMBottomBarItem v-if="splitModeUnit" class="red-button" icon="arrow_back" @click="exitSplitMode()">
      Exit Split
    </SMBottomBarItem>
    <SMBottomBarItem v-if="resizedUnit" class="red-button" icon="arrow_back" @click="stopResizing">
      Exit Resize
    </SMBottomBarItem>
    <SMBottomBarItem
      v-for="(item, key) in options"
      :key="key"
      :class="item.mode === mode ? 'shadow-inner shadow-gray-400 bg-gray-300 hover:bg-gray-300' : ''"
      :icon="item.icon"
      @click="setMode(item.mode)"
    >
      {{ item.name }}
    </SMBottomBarItem>
    <SMBottomBarItem
      v-if="isLayoutSelected && !isInsideZoneLayout && !isEventLiveOrPaused"
      class="red-button"
      icon="extension"
      @click="openDialog(backToLayoutsDialog)"
    >
      Start Over
    </SMBottomBarItem>

    <template #buttons>
      <SMIconButton
        v-if="isLayoutSelected && !isInsideZoneLayout"
        id="cancelseatmap"
        class="green-button back"
        icon="arrow_back"
        title="Back"
        @click="cancelSeatmap"
      />
      <SMIconButton
        v-if="isLayoutSelected && !isInsideZoneLayout"
        class="green-button save"
        icon="save"
        title="Save"
        :indicator="hasUnsavedChanges"
        :loading="savingInProgress"
        @click="saveSeatmap"
      />
      <SMIconButton
        v-if="isLayoutSelected && !isInsideZoneLayout && app.props.showDownloadButton"
        class="green-button save"
        icon="download"
        title="Download"
        @click="downloadSeatmap"
      />
    </template>
    <SMDialogConfirm
      ref="backToLayoutsDialog"
      :md-title="confirm.title"
      :md-content-html="confirm.contentHtml"
      :md-ok-text="confirm.ok"
      :md-cancel-text="confirm.cancel"
      @close="onLayoutDialogClose"
    />
    <SMDialogConfirm
      ref="cancelDialog"
      :md-title="confirmcancel.title"
      :md-content-html="confirmcancel.contentHtml"
      :md-ok-text="confirmcancel.ok"
      :md-cancel-text="confirmcancel.cancel"
      @close="onCancelDialogClose"
    />
    <SMDialogConfirm
      ref="saveDialog"
      :md-title="confirmsave.title"
      :md-content-html="confirmsave.contentHtml"
      :md-ok-text="confirmsave.ok"
      :md-cancel-text="confirmsave.cancel"
      @close="onSaveDialogClose"
    />
    <SMDialog ref="actionsNotFinishedDialog" no-header>
      <template #content>
        {{ splitModeUnit ? 'Please finish splitting first' : 'Please finish resizing first' }}
      </template>
    </SMDialog>
  </SMBottomBar>
</template>
<script lang="ts" setup>
import SMIconButton from '@/components/ui/SMIconButton.vue';
import SMDialog from '@/components/ui/legacy/SMDialog.vue';
import SMDialogConfirm from '@/components/ui/legacy/SMDialogConfirm.vue';
import { DRAWINGMODE, EVENTSTATUS, MODE, SEATSTATUS, TYPE, ZOOM } from '@/constants';
import Application from '@/core/application';
import { CANCELDIALOG, DIALOG, INITIALMODES, SAVEDIALOG } from '@/core/mode/initial/index';
import PathSection from '@/core/models/sections/path';
import { lastUsedSectionFontSize } from '@/core/utils';
import store from '@/store/store';
import { useActions, useGetters, useState } from '@/store/useStore';
import first from 'lodash/first';
import SVG from 'svg.js';
import eventbus from 'tiny-emitter/instance';
import { computed, onBeforeUnmount, onMounted, reactive, ref, shallowRef, watch } from 'vue';

const app = Application.instance;

const options = computed((): { mode: string; icon: string; name: string }[] => {
  const modes = app.props.options.modes;
  let finalModes = INITIALMODES.filter((mode) => modes.includes(mode.name));

  if (app.props.status === EVENTSTATUS.PUBLISHED) {
    finalModes = finalModes.filter((item) => {
      return item.mode === MODE.BLOCKSEATS;
    });
  }

  if (isZonesLayout.value) {
    return finalModes.filter((item) => {
      return ['labels', 'blockseats', 'seats', 'pos', 'group'].indexOf(item.mode) === -1;
    });
  } else if (isInsideZoneLayout.value) {
    return finalModes.filter((item) => {
      return item.mode !== MODE.CATEGORIES;
    });
  }

  return finalModes;
});

const confirm = ref(DIALOG);
const confirmcancel = ref(CANCELDIALOG);
const confirmsave = ref(SAVEDIALOG);
const currentDialog = shallowRef<InstanceType<typeof SMDialogConfirm>>();
const savingInProgress = ref(false);
const errorInPauseModeSaving = ref(false);
const backToLayoutsDialog = shallowRef<InstanceType<typeof SMDialogConfirm>>();
const cancelDialog = shallowRef<InstanceType<typeof SMDialogConfirm>>();
const saveDialog = shallowRef<InstanceType<typeof SMDialogConfirm>>();

const { mode, layout, drawingMode } = useState('settings');
const {
  showLabels,
  showLocks,
  bgImage,
  bgImageScale,
  bgImageOpacity,
  bgImageShowInRenderer,
  isBgUploaded,
  splitModeUnit,
  resizedUnit,
} = useState('seatmap');
const { showGrid, snapToGrid, seatmapName, scale } = useState('camera');
const { isLayoutSelected, isInsideZoneLayout, isZonesLayout } = useGetters('settings');

const isEventLiveOrPaused = computed(() => {
  return app.props.status === EVENTSTATUS.PUBLISHED || app.props.status === EVENTSTATUS.PAUSED;
});
const isEventDraft = computed(() => {
  return app.props.status === EVENTSTATUS.DRAFT;
});
const hasUnsavedChanges = computed(() => {
  return store.state.seatmap.unsavedChanges;
});

watch(
  () => isInsideZoneLayout.value,
  (value) => {
    if (value) {
      const firstMode = first(options.value)!;
      store.commit('settings/mode', firstMode.mode);
    } else {
      store.commit('settings/mode', MODE.DRAWING);
    }
  }
);

// FIXME: This creates the very first mode. This shouldnt be here
onMounted(() => {
  eventbus.on('seatmap:exitZone', exitSelectedZone, this);

  // add event listener for seatmap:saved
  document.addEventListener('seatmap:saved', afterSeatmapSaved);

  const lthis = this;
  let resetMode = true;
  if (options.value.length > 0) {
    if (mode) {
      options.value.forEach((v: { mode: any }) => {
        if (v.mode === mode.value) resetMode = false;
      });
    }
    if (resetMode) {
      const firstMode = first(options.value)!;
      store.commit('settings/mode', firstMode.mode);
    }
  } else {
    store.commit('settings/mode', MODE.DRAWING);
  }
});

onBeforeUnmount(() => {
  eventbus.off('seatmap:exitZone', exitSelectedZone);
  document.removeEventListener('seatmap:saved', afterSeatmapSaved);
});

const {
  setZonesLayout,
  setLayoutChooserMode,
  setDrawingMode,
  changeMode,
  changeDrawingMode,
  enableSelection,
  setSelection,
} = useActions('settings');
const { disableSplitMode } = useActions('seatmap');

const afterSeatmapSaved = (response: any) => {
  const resp = response && response.detail ? response.detail : null;
  let notificationMessage;
  let notificationType;
  if (!resp || resp.status === false || resp.status === null) {
    notificationMessage = resp.msg ? resp.msg : 'Error saving seatmap';
    notificationType = 'error';
    store.commit('seatmap/updateUnsavedChanges', true);
  } else {
    notificationMessage = 'Seatmap Saved';
    notificationType = 'success';
  }
  if (typeof notificationMessage === 'object') {
    let msgHTML = '<ul>';
    notificationMessage.forEach((v: any) => {
      msgHTML += `<li>${v}</li>`;
    });
    msgHTML += '</ul>';
    notificationMessage = msgHTML;
  }
  savingInProgress.value = false;
  store.commit('seatmap/setMessage', { message: notificationMessage, type: notificationType });
  setTimeout(() => {
    store.commit('seatmap/setMessage', { message: '', type: 'error' });
  }, 5000);
};

const actionsNotFinishedDialog = ref<InstanceType<typeof SMDialog>>();
const checkActionsComplete = () => {
  if (splitModeUnit.value || resizedUnit.value) {
    actionsNotFinishedDialog.value?.open();
    return false;
  }

  app.seatmap.sections.forEach((s) => {
    s.hideInsides();
  });
  return true;
};

const downloadSeatmap = () => {
  if (!checkActionsComplete()) {
    return;
  }

  let bgImage;
  if (app.bgImage && app.bgImage.src) {
    const bgImageOpacity = app.bgImage.opacity();
    const bgImageScale = (app.bgImage.transform('scaleX' as any) as unknown as number) / ZOOM.LEVELS.SECTION;
    const x = app.bgImage.x();
    const y = app.bgImage.y();
    const transform = app.bgImage.transform();
    bgImage = {
      bgImageScale,
      bgImageOpacity,
      bgImageShowInRenderer,
      isBgUploaded,
      x: x,
      y: y,
      transform: transform,
      bgImage: app.bgImage.src,
    };
  }

  const settings = reactive({
    scale,
    layout,
    mode,
    drawingMode,
    showLabels,
    showLocks,
    showGrid,
    snapToGrid,
    seatmapName,
    lastUsedSectionFontSize,
    bgImage,
  });

  eventbus.emit('seatmap:download', settings);
};
const saveSeatmap = () => {
  if (!checkActionsComplete()) {
    return;
  }

  let errmsg = '';
  eventbus.emit('seatmap:updated', app.seatmap.seats, app.seatmap.units, app.seatmap.sections);
  //Unlabeled seats
  if (app.unlabeled > 0) {
    errmsg += 'There are ' + app.unlabeled + ' unlabeled seats </br>';
  }
  //Conflicting seats
  if (app.conflicted > 0) {
    errmsg += 'There are ' + app.conflicted + ' conflicting seat labels </br>';
  }
  //Unassigned seats (categories)
  if (app.unassigned > 0) {
    errmsg += 'There are ' + app.unassigned + ' uncategorized seats </br>';
  }
  //Unassigned ga (categories)
  if (app.unassignedGa > 0) {
    errmsg += 'There are ' + app.unassignedGa + ' uncategorized GA </br>';
  }
  const unassignedSections = app.unassignedSections.filter((u) => !u.attributes.invisible).length;
  if (unassignedSections > 0) {
    errmsg += 'There are ' + unassignedSections + ' uncategorized sections </br>';
  }

  //if cannot save
  if (errmsg) {
    if (app.props.status === EVENTSTATUS.PUBLISHED || app.props.status === EVENTSTATUS.PAUSED) {
      confirmsave.value.title = "Can't save changes. Correct errors first";
      confirmsave.value.ok = 'OK';
      confirmsave.value.cancel = '';
      errorInPauseModeSaving.value = true;
    } else {
      errorInPauseModeSaving.value = false;
      confirmsave.value.title = 'Seatmap has errors, save it anyway?';
    }
    confirmsave.value.contentHtml = errmsg;
    //eventbus.emit('menu:hide');
    openDialog(saveDialog.value);
  } else {
    //eventbus.emit('menu:hide');
    onSaveDialogClose('ok');
  }
};
const addKeyListenerForModal = () => {
  SVG.on(window, 'keydown', enterKeyDownHandler, this);
};

const removeKeyListenerForModal = () => {
  currentDialog.value = undefined;
  SVG.off(window, 'keydown', enterKeyDownHandler);
};
const enterKeyDownHandler = (e: { keyCode: string }) => {
  if (parseInt(e.keyCode, 10) === 13 && currentDialog.value != undefined && currentDialog.value != null) {
    const thisDialog = currentDialog;
    switch (thisDialog.value) {
      case backToLayoutsDialog.value:
        onLayoutDialogClose('ok');
        break;
      case cancelDialog.value:
        onCancelDialogClose('ok');
        break;
      case saveDialog.value:
        onSaveDialogClose('ok');
        break;
      default:
    }
    closeDialog(thisDialog.value);
  }
};
const cancelSeatmap = () => {
  if (hasUnsavedChanges.value) {
    openDialog(cancelDialog.value);
  } else if (typeof app.props.onCancel === 'function') {
    app.props.onCancel();
  }
};
const openDialog = (ref: InstanceType<typeof SMDialogConfirm> | undefined) => {
  addKeyListenerForModal();
  currentDialog.value = ref;
  if (!ref) return;
  ref.open();
};
const closeDialog = (ref: InstanceType<typeof SMDialogConfirm> | undefined) => {
  removeKeyListenerForModal();
  currentDialog.value = undefined;
  if (!ref) return;
  ref.close();
};

const onLayoutDialogClose = (type: string) => {
  removeKeyListenerForModal();
  if (type === 'ok') {
    if (
      app.props.status === EVENTSTATUS.DRAFT &&
      !app.seatmap.seatsAndTableSeats.some((s) => s.attributes.status !== SEATSTATUS.FREE)
    ) {
      app.seatmap.reset();
      setLayoutChooserMode();
    } else if (app.seatmap.seatsAndTableSeats.some((s) => s.attributes.status === SEATSTATUS.FREE)) {
      const notificationType = 'error';
      const notificationMessage = 'Something went wrong. Cannot start over';
      store.commit('seatmap/setMessage', { message: notificationMessage, type: notificationType });
      setTimeout(() => {
        store.commit('seatmap/setMessage', { message: '', type: 'error' });
      }, 5000);
    }
  }
};
const onCancelDialogClose = (type: string) => {
  removeKeyListenerForModal();
  if (type === 'ok') {
    onSaveDialogClose(type);
    store.commit('seatmap/updateUnsavedChanges', false);
  } else if (type === 'cancel') {
    store.commit('seatmap/updateUnsavedChanges', false);
  }
  if (typeof app.props.onCancel === 'function') {
    app.props.onCancel();
  }
};
const onSaveDialogClose = (type: string) => {
  removeKeyListenerForModal();
  const unassignedSections = app.unassignedSections.filter((u) => !u.attributes.invisible).length;
  if (type === 'ok') {
    let canSave = false;
    if (app.props.status === EVENTSTATUS.PUBLISHED || app.props.status === EVENTSTATUS.PAUSED) {
      //When the status is published save only if no validation errors.
      //else show warnings and cancel the save.
      if (app.unassigned > 0 || app.conflicted > 0 || app.unlabeled > 0 || app.unassignedGa > 0 || unassignedSections) {
        canSave = false;
      } else {
        savingInProgress.value = true;
        canSave = true;
      }
    } else {
      savingInProgress.value = true;
      //When the status is unpublished, show validation error and seatmap can be saved as draft.
      canSave = true;
    }

    if (app.props.sections) {
      const serverSectionIds = app.props.sections.items.map((s) => s.id);
      const cannotSave = app.seatmap.sections.some(
        (s) =>
          !s.attributes.invisible &&
          s.attributes.serverSectionId &&
          !serverSectionIds.includes(s.attributes.serverSectionId)
      );
      canSave = canSave && !cannotSave;
      savingInProgress.value = false;
    }

    if (canSave) {
      const settings = reactive<Record<string, any>>({
        layout,
        mode,
        scale,
        drawingMode,
        showLabels,
        showLocks,
        showGrid,
        snapToGrid,
        lastUsedSectionFontSize,
        seatmapName,
      });

      if (app.bgImage && app.bgImage.src) {
        const bgImageOpacity = app.bgImage.opacity();
        const bgImageScale = (app.bgImage.transform('scaleX' as any) as unknown as number) / ZOOM.LEVELS.SECTION;
        const x = app.bgImage.x();
        const y = app.bgImage.y();
        const transform = app.bgImage.transform();
        settings.bgImage = {
          bgImageScale,
          bgImageOpacity,
          bgImageShowInRenderer,
          isBgUploaded,
          x: x,
          y: y,
          transform: transform,
          bgImage: app.bgImage.src,
        };
      }

      if (app.unlabeled > 0) {
        settings.unlabeledseats = app.unlabeled;
      }
      //Conflicting seats
      if (app.conflicted > 0) {
        settings.labelConflicts = app.conflicted;
      }
      //Unassigned seats (categories)
      if (app.unassigned > 0) {
        settings.unassignedCats = app.unassigned;
      }
      // Unassigned sections
      if (unassignedSections) {
        settings.unassignedSections = unassignedSections;
      }
      const seatmapJson = app.seatmap.toJSON();
      const saveSettings = Object.assign({}, settings);
      delete saveSettings.seatmapName; // Remove seatmapName
      const json = {
        seatmap: { ...seatmapJson, ...saveSettings },
        version: app.props.json ? app.props.json.version : 2,
      };
      store.commit('seatmap/updateUnsavedChanges', false);

      if (!app.props.onSave) {
        throw new Error('onSave callback is not defined');
      }

      const maybePromise = app.props.onSave(
        app.props.id,
        seatmapName.value,
        JSON.stringify(json),
        app.props.status,
        app.props.extras
      );

      Promise.resolve(maybePromise).then((newData) => {
        if (newData.status) {
          window.parent.location.reload();
        }
      });
      // .then((newData) => {
      //   app.props = {
      //     ...app.props,
      //     json: newData.json,
      //   };
      //   initSeatmap(app, true);
      // });
    }
  }
};
const exitSelectedZone = () => {
  if (isInsideZoneLayout) {
    exitZone();
  }
};
const exitZone = () => {
  // goes back t odrawing tab so that the events that handle exiiting the section are handled correctly
  setDrawingMode();
  setZonesLayout();
  eventbus.emit('seatmap:labels:updated');
  changeDrawingMode(DRAWINGMODE.MAIN);
  enableSelection();
  eventbus.emit('seatmap:exitZone:after');
};
const setMode = (mode: any, enableSelection = true) => {
  changeMode(mode);
  setSelection(enableSelection);
};

const isActiveMode = (mode: any) => {
  return mode === mode;
};

const stopResizing = () => {
  const section = app.seatmap.sections.find((u) => u.uuid === resizedUnit.value)!;

  if (section.subtype === TYPE.PATH) {
    (section as PathSection).finishEdit();
  } else {
    section.endResizing();
  }
};

const exitSplitMode = () => {
  const section = app.seatmap.sections.find((u) => u.uuid === splitModeUnit.value);

  if (!section) return;

  section.hideInsides();
  disableSplitMode();

  enableSelection();
  app.seatmap.selection.add(section);
  eventbus.emit('selection:updated', app.seatmap.selection);
};
</script>
<style lang="scss">
$break-width-top-menu: 1500px;
$make-top-bar-relative-width: 1300px;

.md-bottom-bar {
  box-shadow: none !important;
  border-bottom: 1px solid #d9d9d9;
}

.md-bottom-bar.large-menu {
  @media screen and (max-width: $break-width-top-menu) {
    justify-content: left;
  }
  .md-bottom-bar-item:first-child {
    @media screen and (max-width: $break-width-top-menu) {
      margin-left: 50px;
    }
  }
  .top-right {
    @media screen and (max-width: $make-top-bar-relative-width) {
      position: relative !important;
    }
  }
}

.md-dialog-confirm {
  &.md-active {
    z-index: 200;
  }
}

.md-theme-default.md-bottom-bar.md-fixed {
  .top-right {
    position: absolute;
    right: 0;
    margin: 10px 0;
    .md-bottom-bar-item.green-button {
      font-size: 18px;
      font-weight: 500;
      color: white !important;
      background: #1ab394;
      margin: auto;
      display: inline-flex;
      margin-right: 10px;
      border-radius: 3px;
      &:hover {
        filter: brightness(95%);
      }
      &.back {
        background: white;
        border: 1px solid #e7eaec;
        color: #333333 !important;
      }
      &.md-active {
        padding-top: 8px;
        .md-text {
          transform: scale(0.8571) translateY(2px);
        }
      }
    }
  }
}

.red-button {
  color: #ed5565 !important;
}
</style>
